/**
 * Progresso padronizado do boot SysFactory — engrenagens + legenda + %.
 * Percentual reflete etapas concluídas (ponderadas); sem avanço artificial.
 * Animação suave só ao fechar em 100% (reveal da tela).
 */

import { PH_APP_BOOT_MESSAGE, appBootProgressBarMarkup } from './app-boot-message.js';

/** @typedef {{ id: string; weight?: number; label?: string }} AppBootProgressStep */

/** @type {AppBootProgressStep[]} */
let activeSteps = [];
/** @type {Set<string>} */
let completedStepIds = new Set();
/** @type {number} */
let manualPercent = 0;
/** @type {boolean} */
let useManualPercent = false;
/** @type {string} */
let currentLabel = PH_APP_BOOT_MESSAGE;
/** @type {number} */
let displayedPercent = 0;
/** @type {number | null} */
let finishAnimTimer = null;
const PROGRESS_BAR_SEL = '.ph-app-boot-progress-bar > span';
const PROGRESS_PCT_SEL = '.ph-app-boot-progress-pct';
const PROGRESS_ROOT_SEL = '.ph-app-boot-progress';
const LABEL_SEL = '.ph-app-boot-label, .ph-hub-boot-label, #phHubLoadCaption';

const FINISH_ANIM_TICK_MS = 55;
const FINISH_ANIM_DELTA = 2.5;

/** @deprecated Creep removido — progresso honesto. Mantido como no-op para compat. */
export function startAppBootProgressCreep(_maxPercent = 88) {
  /* noop */
}

/** @deprecated Creep removido — no-op para compat com holdAppBootProgress. */
function stopProgressCreep() {
  /* noop */
}

function stopFinishAnimation() {
  if (finishAnimTimer != null) {
    window.clearInterval(finishAnimTimer);
    finishAnimTimer = null;
  }
}

function syncDisplayedFromTarget() {
  displayedPercent = Math.max(displayedPercent, resolveAppBootProgressPercent());
}

function tickFinishAnimation() {
  if (displayedPercent >= 100) {
    displayedPercent = 100;
    stopFinishAnimation();
    syncAppBootProgressDom();
    return;
  }
  displayedPercent = Math.min(100, displayedPercent + FINISH_ANIM_DELTA);
  syncAppBootProgressDom();
}

function startFinishAnimation() {
  stopFinishAnimation();
  finishAnimTimer = window.setInterval(tickFinishAnimation, FINISH_ANIM_TICK_MS);
}

/** @param {AppBootProgressStep[]} steps */
export function startAppBootProgress(steps) {
  stopFinishAnimation();
  activeSteps = Array.isArray(steps) ? steps.filter((s) => s && s.id) : [];
  completedStepIds = new Set();
  manualPercent = 0;
  useManualPercent = false;
  displayedPercent = 0;
  currentLabel = activeSteps[0]?.label || PH_APP_BOOT_MESSAGE;
  syncAppBootProgressDom();
}

export function resetAppBootProgress() {
  stopFinishAnimation();
  activeSteps = [];
  completedStepIds = new Set();
  manualPercent = 0;
  useManualPercent = false;
  displayedPercent = 0;
  currentLabel = PH_APP_BOOT_MESSAGE;
}

/**
 * @param {number} percent 0–100
 * @param {string} [label]
 */
export function setAppBootProgress(percent, label) {
  useManualPercent = true;
  manualPercent = Math.max(0, Math.min(100, Math.round(Number(percent) || 0)));
  if (label) currentLabel = String(label);
  syncDisplayedFromTarget();
  syncAppBootProgressDom();
}

/** Congela barra/legenda no overlay (ex.: erro de rede — não revelar shell). */
export function holdAppBootProgress(message) {
  stopFinishAnimation();
  stopProgressCreep();
  useManualPercent = true;
  manualPercent = resolveDisplayedPercent();
  displayedPercent = manualPercent;
  if (message) currentLabel = String(message);
  syncAppBootProgressDom();
}

/**
 * @param {string} stepId
 * @param {string} [label]
 */
function resolveNextBootStepLabel(completedId) {
  const nextStep = activeSteps.find((s) => s.id !== completedId && !completedStepIds.has(s.id));
  return nextStep?.label || null;
}

export function completeAppBootProgressStep(stepId, label) {
  const step = activeSteps.find((s) => s.id === stepId);
  if (step) {
    completedStepIds.add(stepId);
    useManualPercent = false;
    if (label) {
      currentLabel = String(label);
    } else {
      const nextLabel = resolveNextBootStepLabel(stepId);
      if (nextLabel) currentLabel = nextLabel;
      else if (step.label) currentLabel = step.label;
    }
  } else if (label) {
    currentLabel = String(label);
  }
  syncDisplayedFromTarget();
  syncAppBootProgressDom();
}

/** @returns {number} */
export function resolveAppBootProgressPercent() {
  if (useManualPercent) return manualPercent;
  if (!activeSteps.length) return manualPercent;
  const totalWeight = activeSteps.reduce((sum, step) => sum + (step.weight ?? 1), 0);
  if (totalWeight <= 0) return completedStepIds.size ? 100 : 0;
  let doneWeight = 0;
  for (const step of activeSteps) {
    if (completedStepIds.has(step.id)) doneWeight += step.weight ?? 1;
  }
  if (completedStepIds.size >= activeSteps.length) return 100;
  return Math.max(0, Math.min(99, Math.round((doneWeight / totalWeight) * 100)));
}

/** @returns {string} Legenda atual do boot (overlay). */
export function getAppBootProgressLabel() {
  return currentLabel;
}

/** @returns {number} */
function resolveDisplayedPercent() {
  return Math.max(0, Math.min(100, Math.round(displayedPercent)));
}

/** @type {Array<() => void> | null} */
let finishRevealCallbacks = null;

/**
 * Completa em 100%, aguarda a barra encher e executa callback (ex.: esconder overlay).
 * Idempotente — chamadas repetidas enfileiram callbacks no mesmo fechamento.
 * @param {() => void} [onDone]
 */
export function finishAppBootProgressWithReveal(onDone) {
  if (finishRevealCallbacks) {
    if (typeof onDone === 'function') finishRevealCallbacks.push(onDone);
    return;
  }
  finishRevealCallbacks = typeof onDone === 'function' ? [onDone] : [];

  useManualPercent = true;
  manualPercent = 100;
  currentLabel = 'SysFactory · pronto para uso';
  displayedPercent = Math.max(displayedPercent, resolveAppBootProgressPercent(), manualPercent);
  syncAppBootProgressDom();
  startFinishAnimation();

  const deadline = Date.now() + 650;
  let finishInvoked = false;
  /** @type {number | null} */
  let finishFallbackTimer = null;

  const finish = () => {
    if (finishInvoked) return;
    finishInvoked = true;
    if (finishFallbackTimer != null) {
      window.clearTimeout(finishFallbackTimer);
      finishFallbackTimer = null;
    }
    stopFinishAnimation();
    resetAppBootProgress();
    const callbacks = finishRevealCallbacks || [];
    finishRevealCallbacks = null;
    for (const cb of callbacks) {
      try {
        cb();
      } catch {
        /* noop */
      }
    }
  };

  const scheduleFinish = () => {
    if (finishInvoked) return;
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => requestAnimationFrame(finish));
    } else {
      window.setTimeout(finish, 32);
    }
    if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') {
      finishFallbackTimer = window.setTimeout(finish, 720);
    }
  };

  const wait = () => {
    if (resolveDisplayedPercent() >= 100 || Date.now() >= deadline) {
      displayedPercent = 100;
      syncAppBootProgressDom();
      scheduleFinish();
      return;
    }
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(wait);
    else window.setTimeout(wait, 16);
  };
  wait();
}

/** Reexporta markup compartilhado com app-boot-message. */
export { appBootProgressBarMarkup } from './app-boot-message.js';

/**
 * @param {ParentNode | null | undefined} root
 */
export function ensureAppBootProgressMarkup(root) {
  if (!root || typeof root.querySelector !== 'function') return;
  if (root.querySelector(PROGRESS_ROOT_SEL)) return;
  const inner = root.querySelector('.ph-app-boot-overlay-inner, .ph-hub-boot-overlay-inner');
  const host = inner || root;
  host.insertAdjacentHTML('beforeend', appBootProgressBarMarkup());
}

/**
 * @param {ParentNode | null | undefined} root
 */
function paintAppBootProgressRoot(root) {
  if (!root) return;
  const pct = resolveDisplayedPercent();
  const progress = root.querySelector(PROGRESS_ROOT_SEL);
  const bar = root.querySelector(PROGRESS_BAR_SEL);
  const pctEl = root.querySelector(PROGRESS_PCT_SEL);
  if (progress instanceof HTMLElement) {
    progress.setAttribute('aria-valuenow', String(pct));
  }
  if (bar instanceof HTMLElement) {
    bar.style.width = `${pct}%`;
  }
  if (pctEl instanceof HTMLElement) {
    pctEl.textContent = `${pct}%`;
  }
  root.querySelectorAll(LABEL_SEL).forEach((el) => {
    if (el instanceof HTMLElement) el.textContent = currentLabel;
  });
}

/** Atualiza overlays visíveis (#phHubBootOverlay, #phPageBootOverlay, gates de módulo). */
export function syncAppBootProgressDom(doc) {
  const rootDoc = doc ?? (typeof document !== 'undefined' ? document : null);
  if (!rootDoc || typeof rootDoc.querySelectorAll !== 'function') return;
  const roots = [
    rootDoc.getElementById('phHubBootOverlay'),
    rootDoc.getElementById('phPageBootOverlay'),
    ...rootDoc.querySelectorAll('.ph-app-boot-overlay:not(.hidden):not([hidden])'),
  ].filter(Boolean);
  for (const root of roots) {
    ensureAppBootProgressMarkup(root);
    paintAppBootProgressRoot(root);
  }
  const mainCaption = rootDoc.getElementById('phHubLoadCaption');
  if (mainCaption instanceof HTMLElement && rootDoc.body?.classList?.contains('ph-hub-app--booting')) {
    mainCaption.textContent = currentLabel;
  }
}
